import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { fmtRM } from "@/lib/format";
import { StatusBadge, CollectionStateBadge } from "@/components/StatusBadge";
import { Search, PlusCircle, ArrowDownWideNarrow, ArrowUpNarrowWide } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";

const FILTERS = ["All", "Active", "Inactive", "Settled", "Bad Debt"];
const SORTS = [
  { key: "recent" },
  { key: "name" },
  { key: "pending" },
  { key: "outstanding" },
  { key: "pl" },
];

const sortValue = (loan, key) => {
  if (key === "pending") return loan.pendingInterest || 0;
  if (key === "outstanding") return loan.outstanding || 0;
  if (key === "pl") return loan.actualProfitLoss || 0;
  return new Date(loan.dateApplied || 0).getTime(); // recent
};

export default function Loans() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loans, setLoans] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [sort, setSort] = useState("recent");
  const [dir, setDir] = useState("desc");
  const [agentFilter, setAgentFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    // "Inactive" is a derived status of Active loans — fetch Active, filter client-side
    const apiStatus = status === "Inactive" ? "Active" : status;
    api
      .get("/loans", { params: { status: apiStatus, search: search || undefined } })
      .then((res) => setLoans(res.data))
      .finally(() => setLoading(false));
  }, [status, search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const agents = [...new Set(loans.map((l) => l.agent).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base" })
  );

  const statusFiltered =
    status === "Inactive"
      ? loans.filter((l) => l.inactive)
      : status === "Active"
      ? loans.filter((l) => !l.inactive)
      : loans;
  const filteredLoans =
    agentFilter === "All" ? statusFiltered : statusFiltered.filter((l) => (l.agent || "") === agentFilter);
  const sortedLoans = [...filteredLoans].sort((a, b) => {
    const diff =
      sort === "name"
        ? (a.customerName || "").localeCompare(b.customerName || "", undefined, { sensitivity: "base" })
        : sortValue(a, sort) - sortValue(b, sort);
    return dir === "asc" ? diff : -diff;
  });

  return (
    <Layout>
      <PageHeader
        title={t("loans.title")}
        subtitle={t("loans.subtitle")}
        action={
          user?.canEdit ? (
            <Link
              to="/loans/new"
              data-testid="loans-new-btn"
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-md px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2"
            >
              <PlusCircle className="h-4 w-4" /> {t("common.new_loan")}
            </Link>
          ) : null
        }
      />

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            data-testid="loans-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && search.trim()) navigate(`/customer?q=${encodeURIComponent(search.trim())}`); }}
            placeholder={t("loans.search_ph")}
            className="w-full pl-9 pr-3 py-2 bg-white border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none"
          />
        </div>
        <div className="flex items-center gap-1 bg-slate-100 rounded-md p-1">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`loans-filter-${f.replace(" ", "-").toLowerCase()}`}
              onClick={() => setStatus(f)}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                status === f ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-800"
              }`}
            >
              {t(`filter.${f}`)}
            </button>
          ))}
        </div>
        <div className="relative">
          <ArrowDownWideNarrow className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
          <select
            data-testid="loans-sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="appearance-none pl-9 pr-8 py-2 bg-white border border-slate-300 rounded-md text-sm font-medium text-slate-700 focus:ring-2 focus:ring-slate-900 outline-none cursor-pointer"
          >
            {SORTS.map((s) => (
              <option key={s.key} value={s.key}>{t("loans.sort_prefix")} {t(`sort.${s.key}`)}</option>
            ))}
          </select>
        </div>
        {agents.length > 0 && (
          <select
            data-testid="loans-agent-filter"
            value={agentFilter}
            onChange={(e) => setAgentFilter(e.target.value)}
            className="appearance-none px-3 py-2 bg-white border border-slate-300 rounded-md text-sm font-medium text-slate-700 focus:ring-2 focus:ring-slate-900 outline-none cursor-pointer"
            title="Filter by agent"
          >
            <option value="All">{t("loans.agent_all")}</option>
            {agents.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        )}
        <button
          data-testid="loans-sort-direction"
          onClick={() => setDir((d) => (d === "desc" ? "asc" : "desc"))}
          title={dir === "desc" ? "Descending (high → low)" : "Ascending (low → high)"}
          className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
        >
          {dir === "desc" ? <ArrowDownWideNarrow className="h-4 w-4" /> : <ArrowUpNarrowWide className="h-4 w-4" />}
          {dir === "desc" ? t("loans.desc") : t("loans.asc")}
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="loans-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
              <th className="px-4 py-3 font-medium">{t("col.code")}</th>
              <th className="px-3 py-3 font-medium">{t("col.date")}</th>
              <th className="px-3 py-3 font-medium">{t("col.customer")}</th>
              <th className="px-3 py-3 font-medium">{t("col.agent")}</th>
              <th className="px-3 py-3 font-medium text-right">{t("col.loan_apply")}</th>
              <th className="px-3 py-3 font-medium text-right">{t("col.in_hand")}</th>
              <th className="px-3 py-3 font-medium">{t("col.formula")}</th>
              <th className="px-3 py-3 font-medium text-right">{t("col.outstanding")}</th>
              <th className="px-3 py-3 font-medium text-right">{t("col.pending_int")}</th>
              <th className="px-3 py-3 font-medium text-right">{t("col.pl")}</th>
              <th className="px-3 py-3 font-medium">{t("col.collection")}</th>
              <th className="px-3 py-3 font-medium">{t("col.status")}</th>
            </tr>
          </thead>
          <tbody>
            {sortedLoans.map((l) => (
              <tr key={l.code} data-testid={`loan-row-${l.code}`} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3 font-mono-fig font-medium">
                  <Link to={`/loans/${l.code}`} data-testid={`loan-view-${l.code}`} className="text-slate-900 hover:underline">{l.customerCode || l.code}</Link>
                  {l.customerCode && <div className="text-[11px] text-slate-400 font-normal" data-testid={`loan-hqcode-${l.code}`}>HQ {l.code}</div>}
                </td>
                <td className="px-3 py-3 text-slate-500 whitespace-nowrap">{l.dateApplied}</td>
                <td className="px-3 py-3 text-slate-700">
                  <Link to={`/customer?q=${encodeURIComponent(l.customerName)}`} data-testid={`loan-customer-${l.code}`} className="hover:underline hover:text-slate-900">{l.customerName}</Link>
                </td>
                <td className="px-3 py-3" data-testid={`loan-agent-${l.code}`}>
                  {l.agent ? <span className="text-slate-600">{l.agent}</span> : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-3 py-3 text-right font-mono-fig text-slate-800">{fmtRM(l.loanApply)}</td>
                <td className="px-3 py-3 text-right font-mono-fig text-slate-800">{fmtRM(l.inHand)}</td>
                <td className="px-3 py-3 font-mono-fig text-xs text-slate-500">{l.repaymentFormula || "—"}</td>
                <td className="px-3 py-3 text-right font-mono-fig text-slate-800">{fmtRM(l.outstanding)}</td>
                <td className="px-3 py-3 text-right font-mono-fig" data-testid={`loan-pending-${l.code}`}>
                  {l.pendingInterest > 0 ? (
                    <span className="text-red-600 font-semibold">{fmtRM(l.pendingInterest)}</span>
                  ) : (
                    <span className="text-slate-300">—</span>
                  )}
                </td>
                <td className={`px-3 py-3 text-right font-mono-fig ${l.actualProfitLoss >= 0 ? "text-green-600" : "text-red-600"}`}>{fmtRM(l.actualProfitLoss)}</td>
                <td className="px-3 py-3"><CollectionStateBadge state={l.collectionState} /></td>
                <td className="px-3 py-3"><StatusBadge status={l.status} inactive={l.inactive} /></td>
              </tr>
            ))}
            {!loading && sortedLoans.length === 0 && (
              <tr><td colSpan={12} className="px-4 py-12 text-center text-slate-400">{t("loans.none")}</td></tr>
            )}
            {loading && (
              <tr><td colSpan={12} className="px-4 py-12 text-center text-slate-400 animate-pulse">{t("common.loading")}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
